import { Injectable } from '@angular/core';
import { FeatureFlag } from 'app/viewmodels/configuration/feature-flag';
import { ConfigService } from '../core/config.service';

export const FEATURE_TOGGLES = {
  /**
   * Whether or not to show the Admin > Maintenance Strategies page and related functionality
   */
  adminMaintenanceStrategy: 'admin-maintenance-strategy',
  /*
  * Enables New bold desk links in about model
  */
  boldDeskLinks: 'BoldDesk-Links',
  /**
   * Build/Nodes
   */
  buildNodes: 'buildNodes',
  /**
   * Data Upload Line List Option
   */
  dataUploadLineListOption: 'data-upload-line-list-option',
  /**
   * Hierarchy Templates New
   */
  hierarchyTemplatesCopy: 'hierarchy-template-copy',
  /*
  * Enables Inapcetion Report Summary
  */
  inspectionReportSummary: 'inspection-reports-summary',
  /**
   * Licensing V2
   */
  licensingV2: 'licensing-v2',
  /*
  * Display cognite Icon
  */
  linktocognite: 'link-to-cognite',
  /*
  * Display Newton Icon
  */
  linktointelligence: 'link-to-intelligence',
  /**
   * Location Asset Hub in Manage Equipment > Hierarchy
   */
  locationAssetHub: 'location-asset-hub',
  /*
  * Enables Generate Mitigations Checkbox
  */
  massCriticalityGeneratePlan: 'mass-criticality-generate-plan',
  /**
   * Mass Criticaltity functionality generate recommendations
   */
  massCriticalityGenerateRecommendations: 'mass-criticality-generate-recommendations',
  /**
   * Mass Criticality Templates application and related functionality
   */
  massCriticalityTemplates: 'mass-criticality-templates',
  /**
   * Mass Criticaltity Results application and related functionality
   */
  massRiskResultsModelDropdown: 'mass-risk-results-model-dropdown',
  /**
   * Mass Risk functionality update assets before run
   */
  massRiskUpdateOperations: 'mass-risk-update-operations',
  /**
   * Mitigation Availability
   */
  mitigationAvailability: 'mitigation-availability',
  /**
   * Model Clone feature
   */
  modelClone: 'model-clone',
  /**
   * permissions cached in read store tables
   */
  permissionCache: 'permission-cache',
  /**
   * RBI/ Risk Projection
   */
  riskProjection: 'rbi-projection',
  /*
   * RBI Summary Report on Report Type selection the Reports Page
   */
  rbiSummaryReport: 'rbi-summary-report',
  /*
   * Regionalisation/Translation feature.
   */
  regionalisation: 'regionalisation',
  /*
  * Enables risk analysis summary report
  */
  riskAnalysisSummary: 'Risk-Analysis-Summary',
  /**
   * Mass Risk run functionality on mass risk page
   */
  massRiskRun: 'risk-mass-run',
  /*
  * enable Thickness Location Move
  */
  thicknesslocationmove: 'thickness-location-move',
  /*
  * Enables events button in Inspection Report Page Actions
  */
  eventsInspectionReports: 'events-inspection-reports',
};

/**
 * Service to get application feature flags
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  private features: FeatureFlag[];

  constructor(configService: ConfigService) {
    const remoteConfig = configService.getRemoteConfiguration();

    if (remoteConfig && remoteConfig.featureFlags) {
      this.features = remoteConfig.featureFlags;
    }
  }

  /**
   * Gets a value indicating if the feature is enabled
   * @param feature Name of the feature
   */
  public isEnabled(feature: string): boolean {
    const wildcardFlag = this.features.find(f => f.key === '*');

    if (wildcardFlag) {
      return wildcardFlag.enabled;
    }

    const flag = this.features.find(f => f.key === feature);

    return flag ? flag.enabled : false;
  }

  public maintenanceStrategyEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.adminMaintenanceStrategy);
  }

  public licensingV2Enabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.licensingV2);

  /**
   * Gets a flag indicating if the Location Asset Hub feature is enabled.
   */
  public locationAssetHubEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.locationAssetHub);
  }

  /**
   * Gets a flag indicating if the Mass Risk Results Model Selection dropdown feature is enabled
   */
  public massRiskResultsModelDropdownEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massRiskResultsModelDropdown);
  }

  /**
   * Gets a flag indicating if the Mass Criticality results feature is enabled
   */
  public massCriticalityGenerateRecommendationsEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massCriticalityGenerateRecommendations);
  }

  /**
   * Gets a flag indicating if the Mass Criticality Templates feature is enabled
   */
  public massCriticalityTemplatesEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massCriticalityTemplates);
  }

  /**
  * Gets a flag indicating if the Mass Risk Run functionality is available on Mass Risk pages
  */
  public massRiskRunEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massRiskRun);
  }

  /**
   * Gets a flag indicating if the model-clone feature is enabled
   */
  public modelCloneEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.modelClone);
  }

  /**
   * Gets a flag indicating if the RBI summary report feature is enabled
   */
  public rbiSummaryReportEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.rbiSummaryReport);
  }

  public regionalisationEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.regionalisation);
  }

  /**
   * Gets a flag indicating if the Data Upload Line List Option feature is enabled
   */
  public dataUploadLineListOptionEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.dataUploadLineListOption);
  }

  /**
   * Gets a flag indicating if the build nodes feature is enabled
   */
  public buildNodesEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.buildNodes);
  }

  /**
   *
   * @returns Gets a flag indicating if the mass risk update opearions feature is enabled
   */
  public massRiskUpdateOperationsEnabled(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.massRiskUpdateOperations);
  }

  /**
   * Gets a flag indicating if the permission cache feature is enabled
   */
  public permissionCacheEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.permissionCache);

  /**
   * Gets a flag indicating if the feature hierarchy-templates-Copy is enabled
   */
  public hierarchyTemplatesCopyEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.hierarchyTemplatesCopy);
  /**
    * Gets a flag indicating if the feature Mitigation Availability is enabled
    */

  public mitigationAvailabilityEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.mitigationAvailability);


  /**
   * Get a flag indicating if the feature rbi-projection is enable
   */
  public riskProjectionEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.riskProjection);

  /**
  * Gets a flag indicating to display newton icon or not
  */
  public linktointelligenceEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.linktointelligence);

  /**
  ** Gets a flag indicating to enable Thickness Location Move or not
 */
  public thicknessLocationMoveEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.thicknesslocationmove);
  /**
  * Gets a flag indicating to display cognite icon or not
  */
  public linktoCogniteEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.linktocognite);



  public inspectionReportsSummary(): boolean {
    return this.isEnabled(FEATURE_TOGGLES.inspectionReportSummary);
  }

  public boldDeskLinksEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.boldDeskLinks);
  /*
  * Gets a flag indicating to risk Analysis Summary Enabled
  */
  public riskAnalysisSummaryEnabled = (): boolean => this.isEnabled(FEATURE_TOGGLES.riskAnalysisSummary);
  /*
  * Gets a flag indicating to generate mass criticality plan  Enabled
  */
  public massCriticalityGeneratePlan = (): boolean => this.isEnabled(FEATURE_TOGGLES.massCriticalityGeneratePlan);
  /*
  * Gets a flag indicating to show events in inspection reports
  */
  public eventsInspectionReports = (): boolean => this.isEnabled(FEATURE_TOGGLES.eventsInspectionReports);

}
